import { useEffect, useState, useCallback } from "react";
import configEntzy from "components/config/ConfigEntzy";

const MINS = 3;
const MS = MINS * 60 * 1000;

export function useMainVersion(intervalMs = MS) {
  const [online, setOnline] = useState(null);
  const [hasUpdate, setHasUpdate] = useState(false);
  const [latestVersion, setLatestVersion] = useState(null);

  const check = useCallback(async () => {
    try {
      const res = await fetch(`/health.json?ts=${Date.now()}`, {
        cache: "no-store",
      });
      if (!res.ok) throw new Error("bad status");
      const data = await res.json();
      const { version, environment, region } = data;

      if (!configEntzy.IS_PRODUCTION) {
        console.log(
          `Latest version: ${version} (env: ${environment}, region: ${region})`,
        );
        console.log(
          `Current version: ${configEntzy.APP_VERSION} (env: ${configEntzy.APP_ENV}, region: ${configEntzy.APP_REGION})`,
        );
      }

      setLatestVersion(version || null);
      setOnline(true);

      if (
        version &&
        version.trim() !== (configEntzy.APP_VERSION || "").trim()
      ) {
        setHasUpdate(true);
      } else {
        setHasUpdate(false);
      }
    } catch (e) {
      console.warn("Version health check failed");
      setOnline(false);
      setHasUpdate(false);
    }
  }, []);

  // initial and interval checks
  useEffect(() => {
    if (configEntzy.IS_LOCAL) {
      console.warn("Local environment detected: skipping version check");
      setOnline(true);
      setHasUpdate(false);
      return;
    }

    check();

    const id = setInterval(check, intervalMs);
    return () => clearInterval(id);
  }, [check, intervalMs]);

  // online/offline event listeners
  useEffect(() => {
    const handleOnline = () => check();
    const handleOffline = () => setOnline(false);

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);
    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
    };
  }, [check]);

  return [online, hasUpdate, latestVersion];
}

// USAGE
// const [online, hasUpdate, latestVersion] = useVersionChecker();
// online: null (unknown), true (online), false (offline)
// hasUpdate: boolean indicating if a newer version is available
// latestVersion: string of the latest version from the server
